import type {
  HTTPServiceDefaults,
  HttpServiceResponse,
  RequestOptions,
} from '@gl/lit/types/http_service.js';
import {
  DuplicateRequestPolicy,
  RequestStatus,
} from '@gl/lit/types/http_service.js';
import {isAbortError} from '@gl/lit/utils/errors.js';
import {getDefaultHeaders} from '@gl/lit/utils/http.js';
import type {UUID} from '@gl/lit/utils/uuid.js';
import {createId} from '@gl/lit/utils/uuid.js';
import type {HttpServiceError} from './errors.js';
import {
  AbortedRequestError,
  DuplicatedRequestError,
  NetworkError,
  TimeoutRequestError,
  isNetworkError,
  isTimeoutError,
} from './errors.js';
import {left, right} from '@gl/lit/types/Either.js';
import {THIRTY_SECOND_MS} from '@gl/lit/common/constants/index.js';
import {isNil, isPrimitive, isString, sleep} from '@gl/lit/utils/index.js';
import {getRandom} from '@gl/lit/utils/numbers.js';

interface HttpServiceRequestItem<T = unknown> {
  url: string;
  requestId: UUID;
  requestKey: string;
  maxRetries: number;
  status: RequestStatus;
  timestamp: {
    start: number;
    end?: number;
    duration?: number;
  };
  controller: AbortController;
  params: T;
}

type NormalizedRequestOptions = Omit<RequestInit, 'body'> & {
  /** customize fetch parameter */
  body?: FormData | Blob | string | null;
  // Custom parameters
  /** @todo (Thiago) : Add support for automatic conversion */
  // responseParser?: 'json' | 'formdata' | 'text' | 'blob' | null;
  requestKey: string;
  requestId: UUID;
  remainingRetries: number;
  referrer: string;
  referrerPolicy: string;
  maxRetries: number;
  duplicateRequestPolicy: DuplicateRequestPolicy;
  timeout: number;
};

const ABORTED_DUPLICATED_REQUEST_MESSAGE =
  'DUPLICATED_REQUEST_WITH_ABORT_PREVIOUS_POLICY';

export class HTTPService {
  defaults: HTTPServiceDefaults = {
    method: 'GET',
    headers: getDefaultHeaders(),
    mode: 'same-origin',
    credentials: 'same-origin',
    referrer: 'no-referrer',
    referrerPolicy: 'same-origin',
    maxRetries: 3,
    duplicateRequestPolicy: DuplicateRequestPolicy.USE_PREVIOUS,
    timeout: THIRTY_SECOND_MS,
  };

  private currentRequests = new Map<
    HttpServiceRequestItem['requestKey'],
    HttpServiceRequestItem
  >();
  private promises = new Map<string, Promise<unknown>>();

  private endRequest(url: string): void {
    this.currentRequests.delete(url);
    this.promises.delete(url);
  }

  private timeoutPromise(timeout: number): Promise<TimeoutRequestError> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(new TimeoutRequestError());
      }, timeout);
    });
  }

  private updateStatus(
    requestKey: NormalizedRequestOptions['requestKey'],
    status: RequestStatus
  ): void {
    const requestItem = this.currentRequests.get(requestKey);
    if (requestItem) {
      const {start} = requestItem.timestamp;
      const endTimestamp = Date.now();
      requestItem.status = status;
      requestItem.timestamp = {
        start,
        end: endTimestamp,
        duration: endTimestamp - start,
      };
    }
  }

  private async fetch(
    input: RequestInfo | URL,
    init?: NormalizedRequestOptions | undefined
  ): Promise<Response | NetworkError> {
    try {
      const response = await fetch(input, init);
      return response;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return new NetworkError(error.message);
      }
      return new NetworkError(String(error));
    }
  }

  private async runRequest<TSuccess, TError = HttpServiceError>(
    url: string,
    options: NormalizedRequestOptions
  ): HttpServiceResponse<TError, TSuccess> {
    const response = await Promise.race([
      this.fetch(url, options),
      this.timeoutPromise(options.timeout),
    ]);

    if (isNetworkError(response)) {
      return left(response);
    }

    if (isTimeoutError(response)) {
      this.abort(url, 'Timeout');
      return left(response);
    }

    // Status 204 indicated No Content on the response
    // We fallback to null on this case
    const responseValue =
      response.status === 204 ? null : await response.json();

    if (response.ok) {
      return right(responseValue as TSuccess);
    }

    if (options.remainingRetries > 0) {
      const delayTimeForRetry =
        getRandom(10, 150) * (options.maxRetries - options.remainingRetries);
      await sleep(delayTimeForRetry);

      return this.runRequest(url, {
        ...options,
        remainingRetries: options.remainingRetries - 1,
      });
    }

    return left(responseValue as TError);
  }

  private normalizeBody(
    body?: RequestOptions['body']
  ): NormalizedRequestOptions['body'] {
    if (isNil(body)) return body;
    if (isPrimitive(body)) return String(body);
    if (body instanceof FormData) return body;
    return JSON.stringify(body);
  }

  abort(url: string, reason?: string): void {
    const request = this.currentRequests.get(url);
    if (request && request.status === RequestStatus.FETCHING) {
      request.controller.abort(reason);
    }
  }

  async request<TSuccess, TError = HttpServiceError>(
    url: string | URL,
    options?: RequestOptions
  ): HttpServiceResponse<TError, TSuccess> {
    const normalizedUrl = isString(url) ? url : url.toString();
    const normalizedOptions: NormalizedRequestOptions = {
      requestId: createId(),
      requestKey: normalizedUrl,
      ...this.defaults,
      ...options,
      body: this.normalizeBody(options?.body),
      remainingRetries: options?.maxRetries ?? this.defaults.maxRetries,
    };

    const {requestKey} = normalizedOptions;

    if (this.currentRequests.has(normalizedUrl)) {
      if (
        normalizedOptions.duplicateRequestPolicy ===
        DuplicateRequestPolicy.ABORT_PREVIOUS
      ) {
        this.abort(normalizedUrl, ABORTED_DUPLICATED_REQUEST_MESSAGE);
      }

      if (
        normalizedOptions.duplicateRequestPolicy ===
        DuplicateRequestPolicy.USE_PREVIOUS
      ) {
        const previousRequest = this.promises.get(requestKey);
        if (previousRequest) {
          return previousRequest as HttpServiceResponse<TError, TSuccess>;
        }
      }
    }

    this.currentRequests.set(requestKey, {
      url: normalizedUrl,
      requestKey,
      requestId: normalizedOptions.requestId,
      maxRetries: normalizedOptions.maxRetries,
      status: RequestStatus.FETCHING,
      timestamp: {
        start: Date.now(),
      },
      controller: new AbortController(),
      params: normalizedOptions,
    });

    try {
      const promise = this.runRequest<TSuccess, TError>(
        normalizedUrl,
        normalizedOptions
      );
      this.promises.set(requestKey, promise);
      const response = await promise;
      this.updateStatus(requestKey, RequestStatus.SUCCESS);
      return response;
    } catch (error) {
      const abortMessage = `Request "${normalizedUrl}" under key ${requestKey} was aborted.`;

      if (
        error instanceof DOMException &&
        error.message === ABORTED_DUPLICATED_REQUEST_MESSAGE
      ) {
        this.updateStatus(requestKey, RequestStatus.ABORTED);
        return left(new DuplicatedRequestError(abortMessage));
      }

      if (isAbortError(error)) {
        const abortError = new AbortedRequestError(abortMessage);
        abortError.register(abortMessage);
        this.updateStatus(requestKey, RequestStatus.ABORTED);
        return left(abortError);
      }

      this.updateStatus(requestKey, RequestStatus.FAIL);
      return left(error as SyntaxError);
    } finally {
      this.endRequest(normalizedUrl);
    }
  }
}
